import { invariant } from '@seen/core/invariant'
import { RecentSelections } from '@seen/core/models/recentSelections'
import type { View } from '@seen/core/models/view'
import {
  Button,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  cn,
} from '@seen/ui'
import { IconEdit } from '@tabler/icons-react'
import { compact, sortBy, uniq } from 'lodash-es'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { ContextMenuMedplumLinkItem } from '#src/components/ContextMenuMedplumLinkItem.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { ContextMenuDeleteItem } from '#src/components/core/context-menu/ContextMenuDeleteItem.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { ListHeaderSeparator } from '#src/components/ListHeaderSeparator.tsx'
import { ViewComposerDialogContent } from '#src/components/ViewComposerDialogContent.tsx'
import { ViewOption } from '#src/components/ViewOption.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { extractTitleFromHTML } from '#src/utils/html.ts'

export function ViewRow({
  views,
  selectedView,
  onSelectView,
  onDeleteSelectedView,
  className,
}: {
  views: View[] | undefined
  selectedView: string
  onSelectView: (referenceString: View['referenceString']) => void
  onDeleteSelectedView: () => void
  className?: string
}): ReactNode {
  const currentPractitioner = useCurrentPractitioner()
  const { data: recentSelections } = useRead(
    RecentSelections,
    currentPractitioner?.recentSelections
  )
  const recentlySelectedViews = useMemo(
    () =>
      sortBy([...(views ?? [])], (v) => {
        const index = recentSelections?.values.indexOf(v.reference.reference)
        return index === -1 ? Infinity : index
      }),
    [views, recentSelections]
  )

  // We don't want views to move around as the user selects them, but we want them to update when the user selects a view for the first time or selects a view from "All views."
  const [viewOrdering, setViewOrdering] = useState<View['referenceString'][]>(
    recentlySelectedViews.map((v) => v.referenceString)
  )

  useEffect(() => {
    setViewOrdering((prev) => {
      const prevSet = new Set(prev)
      const hasNewSelection = recentlySelectedViews.some(
        (v) => !prevSet.has(v.referenceString)
      )
      return hasNewSelection
        ? recentlySelectedViews.map((v) => v.referenceString)
        : prev
    })
  }, [recentlySelectedViews])

  const viewsToShow = useMemo(
    () =>
      compact(
        viewOrdering.map((viewReferenceString) =>
          views?.find((v) => v.referenceString === viewReferenceString)
        )
      ),
    [viewOrdering, views]
  )

  const [composerOpen, setComposerOpen] = useState(false)
  const [editView, setEditView] = useState<View | undefined>(undefined)

  const patchViewMutation = usePatchMutation<View>()

  const patchRecentSelections = usePatchMutation<RecentSelections>(
    currentPractitioner.recentSelections?.reference
      ? {
          mutation: {
            scope: {
              id: currentPractitioner.recentSelections.reference,
            },
          },
        }
      : undefined
  ).mutate

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {!!viewsToShow.length && <ListHeaderSeparator />}
      <div className="flex max-h-6 flex-1 flex-wrap items-center gap-2 overflow-hidden">
        {viewsToShow.map((view) => (
          <ContextMenu key={view.referenceString}>
            <ContextMenuTrigger asChild>
              <Button
                variant="outline"
                size="xs"
                selected={selectedView === view.referenceString}
                className="max-w-48"
                onClick={() => {
                  onSelectView(view.referenceString)
                  if (recentSelections) {
                    patchRecentSelections({
                      instance: recentSelections,
                      mutateFn: (rs) => {
                        rs.add(view.reference.reference)
                      },
                    })
                  }
                }}
              >
                <span className="truncate">{view.name}</span>
              </Button>
            </ContextMenuTrigger>
            <ContextMenuContent>
              <ContextMenuItem
                className="gap-2"
                onSelect={() => {
                  setEditView(view)
                  setComposerOpen(true)
                }}
              >
                <IconEdit className="size-4" />
                Edit…
              </ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuDeleteItem
                deleteConfirmationProps={{
                  title: `Delete "${extractTitleFromHTML(view.name)}"?`,
                  description:
                    'Deleted views can still be recovered by contacting our team.',
                  onConfirm: () => {
                    patchViewMutation.mutate({
                      instance: view,
                      mutateFn: (v) => v.delete(),
                    })
                    if (selectedView === view.referenceString) {
                      onDeleteSelectedView()
                    }
                  },
                }}
              />
              <ContextMenuMedplumLinkItem to={view} />
            </ContextMenuContent>
          </ContextMenu>
        ))}
      </div>
      <Combobox
        options={recentlySelectedViews.map((v) => ({
          label: (
            <ViewOption
              view={v.reference.reference}
              isSelected={selectedView === v.reference.reference}
            />
          ),
          value: v.reference.reference,
          keywords: [v.name],
        }))}
        onSelect={(viewReferenceString) => {
          const view = recentlySelectedViews.find(
            (v) => v.reference.reference === viewReferenceString
          )
          invariant(view, 'View not found')
          onSelectView(view.referenceString)
          if (recentSelections) {
            patchRecentSelections({
              instance: recentSelections,
              mutateFn: (rs) => {
                rs.add(view.reference.reference)
              },
            })
          }
          setViewOrdering((vo) => uniq([view.referenceString, ...vo]))
        }}
      >
        <Button variant="outline" size="xs">
          All views…
        </Button>
      </Combobox>
      <Dialog open={composerOpen} onOpenChange={setComposerOpen}>
        <DialogDisplay>
          <ViewComposerDialogContent
            view={editView}
            onSubmit={({ name, access }) => {
              invariant(editView, 'editView is required')
              patchViewMutation.mutate({
                instance: editView,
                mutateFn: (v) => {
                  v.name = name
                  v.access = access
                },
              })
              setComposerOpen(false)
            }}
            isSubmitting={patchViewMutation.isPending}
          />
        </DialogDisplay>
      </Dialog>
    </div>
  )
}
